import type { UserProfile, VehicleIdentifier } from '@/types/domain';
import type { ZodSchema } from 'zod';
import { env } from './env';
import { auth } from './firebase';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiFetchOptions<T> extends Omit<RequestInit, 'body'> {
  /** JSON-serialized into the request body when present. */
  body?: unknown;
  /** Zod schema validates the response shape — recommended for every endpoint. */
  schema?: ZodSchema<T>;
}

/**
 * Authenticated typed fetcher for gg-api. Attaches a Firebase ID token,
 * serializes JSON, and throws `ApiError` on non-2xx so TanStack Query can
 * surface server failures cleanly.
 */
export async function apiFetch<T>(path: string, options?: ApiFetchOptions<T>): Promise<T> {
  const user = auth.currentUser;
  if (!user) throw new ApiError(401, 'NO_AUTH', 'Not signed in');

  const token = await user.getIdToken();

  const { body, schema, headers, ...rest } = options ?? {};
  const init: RequestInit = {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  const response = await fetch(`${env.VITE_GG_API_BASE_URL}${path}`, init);

  if (!response.ok) {
    let parsed: { error?: { code?: string; message?: string; details?: unknown } } = {};
    try {
      parsed = (await response.json()) as typeof parsed;
    } catch {
      // non-JSON error body — fall through to status-text
    }
    throw new ApiError(
      response.status,
      parsed.error?.code ?? 'UNKNOWN',
      parsed.error?.message ?? response.statusText,
      parsed.error?.details,
    );
  }

  const data = (await response.json()) as unknown;
  if (schema) {
    return schema.parse(data);
  }
  return data as T;
}

export interface ContestVehicleClaimInput {
  year: number;
  make: string;
  model: string;
  chassisCode?: string;
  identifiers: VehicleIdentifier[];
  note?: string;
}

export interface ContestVehicleClaimResponse {
  contestId: string;
}

/**
 * POST /v1/vehicles/contest — submit a dispute when a VIN is already claimed
 * by a different owner (409 CONFLICT). Creates a manual-review record server-side.
 */
export async function contestVehicleClaim(
  input: ContestVehicleClaimInput,
): Promise<ContestVehicleClaimResponse> {
  return apiFetch<ContestVehicleClaimResponse>('/v1/vehicles/contest', {
    method: 'POST',
    body: input,
  });
}

/**
 * POST /v1/account/bootstrap — idempotent profile creation/hydration.
 *
 * Called on every sign-in. Safe to re-call: server upserts and returns
 * current profile. Captures locale + timezone from the browser.
 */
export async function bootstrapAccount(): Promise<UserProfile> {
  const locale = navigator.language;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return apiFetch<UserProfile>('/v1/account/bootstrap', {
    method: 'POST',
    body: { locale, timezone },
  });
}

/**
 * apiFetchEmpty — like apiFetch but expects a 204 No Content response.
 *
 * Used for DELETE /v1/photos/:id (crypto-shred) and any other endpoint
 * that returns no body on success. Throws ApiError on non-2xx.
 */
export async function apiFetchEmpty(
  path: string,
  options?: Omit<ApiFetchOptions<never>, 'schema'>,
): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new ApiError(401, 'NO_AUTH', 'Not signed in');

  const token = await user.getIdToken();

  const { body, headers, ...rest } = options ?? {};
  const init: RequestInit = {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  const response = await fetch(`${env.VITE_GG_API_BASE_URL}${path}`, init);

  if (!response.ok) {
    let parsed: { error?: { code?: string; message?: string; details?: unknown } } = {};
    try {
      parsed = (await response.json()) as typeof parsed;
    } catch {
      // non-JSON error body — fall through to status-text
    }
    throw new ApiError(
      response.status,
      parsed.error?.code ?? 'UNKNOWN',
      parsed.error?.message ?? response.statusText,
      parsed.error?.details,
    );
  }
}
